// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { Typography } from "@insite/mobius/index";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@steiner/App/Components/Common/XngZone";
import ProductBoxes from "@steiner/App/Components/Product/ProductBoxes";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import { isNonStockProduct } from "@steiner/App/Store/Selectors/ProductDetailsSelector";
import { getSettingsCollection } from "@steiner/App/Store/Selectors/SettingsSelectors";
import { mergeWithCss } from "@steiner/XDL/Utilities/mergeWithCss";
import * as React from "react";
import { FC, useState } from "react";

export interface ProductActionsProps {
    extendedStyles?: ProductActionsStyle;
    widgetId: string;
}

type Props = ProductActionsProps & HasProductContext;

export interface ProductActionsStyle {
    addToCartContainer?: InjectableCss;
    qtyWrapper?: InjectableCss;
    addToCartButtonWrapper?: InjectableCss;
    productUomWrapper?: InjectableCss;
    addToProductGroup?: InjectableCss;
    minQtyMessage?: TypographyPresentationProps;
    addCutLengthsButtonWrapper?: InjectableCss;
}

export const productActionsStyle: ProductActionsStyle = {};

const ProductActions: FC<Props> = ({ extendedStyles, productContext, widgetId }) => {
    const [styles] = useState(() => mergeWithCss(productActionsStyle, extendedStyles));
    const { productSettings, isNonStockItem } = useAppSelector(state => {
        const currentPage = getCurrentPage(state)?.type;
        const isProductDetailsPage = currentPage === "ProductDetailsPage";
        return {
            configurationCompleted: isProductDetailsPage && state.pages.productDetails.configurationCompleted,
            variantSelectionCompleted: isProductDetailsPage && state.pages.productDetails.variantSelectionCompleted,
            configurationSelection: isProductDetailsPage
                ? state.pages.productDetails.configurationSelection
                : undefined,
            subscription: isProductDetailsPage ? state.pages.productDetails.subscription : undefined,
            productSettings: getSettingsCollection(state).productSettings,
            isProductDetailsPage,
            isNonStockItem: isNonStockProduct(state as CustomAppState, productContext.product.id),
        };
    });

    if (productContext.product.isVariantParent || productContext.product.isDiscontinued || isNonStockItem) {
        return null;
    }

    const unitOfMeasures = productContext.product.unitOfMeasures;
    const willShowUnitOfMeasureSelect = !(
        !productSettings?.alternateUnitsOfMeasure ||
        !unitOfMeasures ||
        unitOfMeasures.filter(uom => uom.unitOfMeasure).length <= 1
    );

    if (willShowUnitOfMeasureSelect) {
        return (
            <StyledWrapper {...styles.addToCartContainer} className="has-uom" data-test-selector="product_actions">
                <StyledWrapper {...styles.addToCartContainer}>
                    <StyledWrapper {...styles.qtyWrapper}>
                        <XngZone contentId={widgetId} zoneName="Content09" hint="Qty Ordered" />
                    </StyledWrapper>
                    <StyledWrapper {...styles.productUomWrapper}>
                        <XngZone contentId={widgetId} zoneName="Content10" hint="Unit Of Measure" />
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.addToCartButtonWrapper}>
                    <XngZone contentId={widgetId} zoneName="Content11" hint="Add To Cart" />
                </StyledWrapper>
                <StyledWrapper {...styles.addCutLengthsButtonWrapper}>
                    <XngZone contentId={widgetId} zoneName="Content13" hint="Add Cut Lengths" />
                </StyledWrapper>
                <StyledWrapper {...styles.addToProductGroup}>
                    <XngZone contentId={widgetId} zoneName="Content12" hint="Add To List" />
                </StyledWrapper>
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.addToCartContainer} data-test-selector="product_actions">
            <StyledWrapper {...styles.qtyWrapper}>
                <XngZone contentId={widgetId} zoneName="Content09" hint="Qty Ordered" />
            </StyledWrapper>

            <StyledWrapper {...styles.addToCartButtonWrapper}>
                <XngZone contentId={widgetId} zoneName="Content11" hint="Add To Cart" />
            </StyledWrapper>
            <StyledWrapper {...styles.addCutLengthsButtonWrapper}>
                <XngZone contentId={widgetId} zoneName="Content13" hint="Add Cut Lengths" />
            </StyledWrapper>
            <StyledWrapper {...styles.addToProductGroup}>
                <XngZone contentId={widgetId} zoneName="Content12" hint="Add To List" />
            </StyledWrapper>
            {productContext.product.minimumOrderQty > 1 && (
                <Typography {...styles.minQtyMessage}>
                    {translate(
                        "The minimum package quantity is {0}",
                        productContext.product.minimumOrderQty.toString(),
                    )}
                </Typography>
            )}
            <ProductBoxes />
        </StyledWrapper>
    );
};

export default withProductContext(ProductActions);
